import React from 'react'
import {Link as RouterLink} from "react-router-dom"
import {Breadcrumbs, Link, Skeleton, Tooltip, Typography} from "@mui/material"
import Logo from "../../accounts/Logo"
import {useApi} from "../../../hooks/useApi"
import {BreadcrumbItem, useBreadcrumbsContext} from "./BreadcrumbsProvider"

const MAX_CRUMB_LENGTH = 28

const crumbBaseSx = {fontSize: '0.95rem', fontWeight: 500}
const linkCrumbSx = {...crumbBaseSx, color: '#666', textDecoration: 'none', '&:hover': {color: '#333', textDecoration: 'underline'}}
const textCrumbSx = {...crumbBaseSx, color: '#333'}
const homeLinkSx = {...linkCrumbSx, display: 'flex', alignItems: 'center', gap: 1}

function truncateLabel(label: string): {display: string; truncated: boolean} {
    if (label.length <= MAX_CRUMB_LENGTH) return {display: label, truncated: false}
    return {display: label.slice(0, MAX_CRUMB_LENGTH - 1) + "…", truncated: true}
}

function MaybeTooltip({title, truncated, children}: {title: string; truncated: boolean; children: React.ReactElement}) {
    return truncated ? <Tooltip title={title}>{children}</Tooltip> : children
}

function LoadingCrumb() {
    return <Skeleton variant="text" width={80} sx={{fontSize: '0.95rem'}}/>
}

function TextCrumb({label}: {label: string}) {
    if (!label) return <LoadingCrumb/>
    const {display, truncated} = truncateLabel(label)
    return (
        <MaybeTooltip title={label} truncated={truncated}>
            <Typography sx={textCrumbSx}>{display}</Typography>
        </MaybeTooltip>
    )
}

function LinkCrumb({label, url}: {label: string; url: string}) {
    if (!label) return <LoadingCrumb/>
    const {display, truncated} = truncateLabel(label)
    return (
        <MaybeTooltip title={label} truncated={truncated}>
            <Link component={RouterLink} to={url} sx={linkCrumbSx}>{display}</Link>
        </MaybeTooltip>
    )
}

function renderItem(item: BreadcrumbItem, isLast: boolean) {
    if ("render" in item) return item.render
    if (isLast || !item.url) return <TextCrumb label={item.label}/>
    return <LinkCrumb label={item.label} url={item.url}/>
}

function NavBreadcrumbs() {
    const {currentOrganization} = useApi()
    const {entries} = useBreadcrumbsContext()

    const sorted = Array.from(entries.values()).sort((a, b) => a.depth - b.depth)
    const chain: BreadcrumbItem[] = sorted.flatMap(e => e.items)

    const orgName = currentOrganization?.name ?? ""

    return (
        <Breadcrumbs
            aria-label="breadcrumb"
            separator={<Typography sx={{color: '#ccc', userSelect: 'none'}}>/</Typography>}
            sx={{'& .MuiBreadcrumbs-separator': {mx: 0.75}}}
        >
            <Link key="home" component={RouterLink} to="/" sx={homeLinkSx} aria-label="Home">
                <Logo image={currentOrganization?.logo}/>
                {orgName ? <span>{orgName}</span> : <LoadingCrumb/>}
            </Link>
            {chain.map((item, i) => (
                <React.Fragment key={i}>
                    {renderItem(item, i === chain.length - 1)}
                </React.Fragment>
            ))}
        </Breadcrumbs>
    )
}

export default NavBreadcrumbs
